import React from 'react';
import PropTypes from 'prop-types';
import { GridList, EmptyState, BufferLoading } from '~/shared-components';
import { Button, Text, Tooltip } from '@bufferapp/ui';
import CopyIcon from '@bufferapp/ui/Icon/Icons/Copy';
import InfoIcon from '@bufferapp/ui/Icon/Icons/Info';
import ArrowRightIcon from '@bufferapp/ui/Icon/Icons/ArrowRight';
import Avatar from '@bufferapp/ui/Avatar';
import getErrorBoundary from '~/web/components/ErrorBoundary';
import { IconArrowPopover } from '@bufferapp/components';
import styled from 'styled-components';
import { grayLight, grayDark, gray } from '@bufferapp/ui/style/colors';
import { borderRadius } from '@bufferapp/ui/style/borders';
import { fontFamily, fontSize } from '@bufferapp/ui/style/fonts';
import ProfilesDisconnectedBanner from '~/profiles-disconnected-banner';
import { openPreviewPage } from '../../util';
import CustomLinks from '../CustomLinks';

const ErrorBoundary = getErrorBoundary(true);

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.8rem;
  width: 100%;
`;

const StlyedLoadingGridContainer = styled.div`
  width: 100%;
  height: 100%;
  text-align: center;
  padding-top: 5rem;
`;

const StyledProfileBadge = styled.div`
  padding: 0.05rem 0.25rem;
`;

const StyledProfileHeader = styled.div`
  display: flex;
  align-items: center;
`;

const StyledButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 340px;
`;

const StyledLinkField = styled.div`
  margin-right: 12px;
  width: 100%;
`;

const StlyedCopyLinkButton = styled.button`
  align-items: center;
  background-color: ${grayLight};
  border: 1px solid ${grayLight};
  border-radius: ${borderRadius};
  color: ${grayDark};
  cursor: pointer;
  display: flex;
  font-family: ${fontFamily};
  font-size: ${fontSize};
  height: 40px;
  outline: none;
  padding-left: 16px;
  padding-right: 16px;
  width: 100%;
`;

const StyledCopyLink = styled.div`
  width: 16px;
  height: 16px;
  margin-left: auto;
`;

const StyledCopyLinkIcon = styled.div`
  display: inline-block;
  position: relative;
  top: 0;
  left: 5px;
`;

const StyledCopyLinkText = styled.div`
  padding: 0.25rem 0.1rem;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const Title = styled(Text)`
  color: ${grayDark};
  margin: 16px 0;
`;

const TooltipWrapper = styled.div`
  padding: 6px 0 0 6px;
`;

const onCopyToClipboard = ({ publicGridUrl, handleCopyToClipboard }) => {
  const el = document.createElement('textarea');
  el.value = publicGridUrl;
  el.setAttribute('readonly', '');
  el.style.position = 'absolute';
  el.style.left = '-9999px';
  document.body.appendChild(el);
  el.select();
  const copied = document.execCommand('copy');
  document.body.removeChild(el);
  if (copied) {
    handleCopyToClipboard({ copySuccess: true, publicGridUrl });
  } else {
    handleCopyToClipboard({ copySuccess: false });
  }
};

const onPreviewClick = (publicGridUrl) => {
  openPreviewPage(publicGridUrl);
};

const GridPosts = ({
  total,
  loading,
  gridPosts,
  onImageClick,
  onImageClose,
  onChangePostUrl,
  onSavePostUrl,
  trackPagePreviewed,
  isDisconnectedProfile,
  profile,
  handleCopyToClipboard,
  publicGridUrl,
  previewGridUrl,
  customLinksDetails,
  onUpdateCustomLinks,
  onUpdateCustomLinksColor,
  onUpdateCustomLinksButtonType,
  onDeleteCustomLink,
  onUpdateLinkText,
  onUpdateLinkUrl,
  maxCustomLinks,
  onToggleEditMode,
  onSwapCustomLinks,
  onCancelCustomLinkEdit,
  onSaveNewLinkClick,
  onUpdateSingleCustomLink,
  isValidItem,
  hasWriteAccess,
}) => {
  if (loading) {
    return (
      <StlyedLoadingGridContainer>
        <BufferLoading size={64} />
      </StlyedLoadingGridContainer>
    );
  }

  return (
    <ErrorBoundary>
      {isDisconnectedProfile && <ProfilesDisconnectedBanner />}
      {!isDisconnectedProfile && (
        <div>
          <StyledHeader>
            <StyledProfileHeader>
              <StyledProfileBadge>
                <Avatar
                  src={profile.avatar_https}
                  fallbackUrl="https://s3.amazonaws.com/buffer-ui/Default+Avatar.png"
                  alt={profile.handle}
                  size="large"
                  type="social"
                  network={profile.service}
                />
              </StyledProfileBadge>
            </StyledProfileHeader>
            <StyledButtonWrapper>
              <StyledLinkField>
                <StlyedCopyLinkButton
                  type="button"
                  onClick={() => {
                    onCopyToClipboard({
                      publicGridUrl,
                      handleCopyToClipboard,
                    });
                  }}
                >
                  {publicGridUrl}
                  <StyledCopyLink>
                    <StyledCopyLinkIcon>
                      <IconArrowPopover
                        icon={<CopyIcon size="medium" />}
                        shadow
                        oneLine
                        label="Copy Page Link"
                      >
                        <StyledCopyLinkText>Copy Page Link</StyledCopyLinkText>
                      </IconArrowPopover>
                    </StyledCopyLinkIcon>
                  </StyledCopyLink>
                </StlyedCopyLinkButton>
              </StyledLinkField>
              <Button
                label="Preview Page"
                type="secondary"
                onClick={() => {
                  onPreviewClick(previewGridUrl);
                  trackPagePreviewed(profile);
                }}
                icon={<ArrowRightIcon />}
                iconEnd
              />
            </StyledButtonWrapper>
          </StyledHeader>
          <CustomLinks
            customLinksDetails={customLinksDetails}
            onUpdateCustomLinks={onUpdateCustomLinks}
            onUpdateCustomLinksColor={onUpdateCustomLinksColor}
            onUpdateCustomLinksButtonType={onUpdateCustomLinksButtonType}
            onDeleteCustomLink={onDeleteCustomLink}
            onUpdateLinkText={onUpdateLinkText}
            onUpdateLinkUrl={onUpdateLinkUrl}
            maxCustomLinks={maxCustomLinks}
            onToggleEditMode={onToggleEditMode}
            onSwapCustomLinks={onSwapCustomLinks}
            onCancelCustomLinkEdit={onCancelCustomLinkEdit}
            onSaveNewLinkClick={onSaveNewLinkClick}
            onUpdateSingleCustomLink={onUpdateSingleCustomLink}
            isValidItem={isValidItem}
            hasWriteAccess={hasWriteAccess}
          />
          <TitleWrapper>
            <Title type="h3" color="grayDark">
              Posts
            </Title>
            <TooltipWrapper>
              <Tooltip
                label="Only posts with links will appear on your public Shop Grid page."
                position="right"
              >
                <InfoIcon color={gray} />
              </Tooltip>
            </TooltipWrapper>
          </TitleWrapper>
          {total < 1 && (
            <EmptyState
              height="auto"
              title="You haven’t published any posts to this Instagram account yet."
              subtitle="Once you’ve posted to your Instagram feed, use Shop Grid as your link in bio tool to drive traffic from Instagram to anywhere you’d like — your online store, articles, events, and more!"
              heroImg="https://s3.amazonaws.com/buffer-publish/images/empty-sent2x.png"
              heroImgSize={{ width: '270px', height: '150px' }}
            />
          )}
          {total >= 1 && (
            <GridList
              gridPosts={gridPosts}
              onChangePostUrl={onChangePostUrl}
              onSavePostUrl={onSavePostUrl}
              onImageClick={onImageClick}
              onImageClose={onImageClose}
              timezone={profile.timezone}
              hasWriteAccess={hasWriteAccess}
            />
          )}
        </div>
      )}
    </ErrorBoundary>
  );
};

GridPosts.propTypes = {
  loading: PropTypes.bool,
  page: PropTypes.number, // eslint-disable-line
  gridPosts: PropTypes.arrayOf(
    PropTypes.shape({
      posts: PropTypes.arrayOf(
        PropTypes.shape({
          text: PropTypes.string,
        })
      ),
    })
  ),
  total: PropTypes.number,
  onChangePostUrl: PropTypes.func,
  onSavePostUrl: PropTypes.func,
  onImageClick: PropTypes.func,
  onImageClose: PropTypes.func,
  handleCopyToClipboard: PropTypes.func,
  trackPagePreviewed: PropTypes.func.isRequired,
  publicGridUrl: PropTypes.string,
  previewGridUrl: PropTypes.string,
  isDisconnectedProfile: PropTypes.bool,
  profile: PropTypes.shape({
    service: PropTypes.string,
    avatar_https: PropTypes.string,
    timezone: PropTypes.string,
    handle: PropTypes.string,
  }),
  customLinksDetails: PropTypes.shape({
    customLinks: PropTypes.array,
    maxCustomLinks: PropTypes.number,
    buttonColor: PropTypes.string,
  }),
  isValidItem: PropTypes.func,
  hasWriteAccess: PropTypes.bool,
  maxCustomLinks: PropTypes.number,
  onUpdateCustomLinks: PropTypes.func,
  onUpdateCustomLinksColor: PropTypes.func,
  onUpdateCustomLinksButtonType: PropTypes.func,
  onDeleteCustomLink: PropTypes.func,
  onUpdateLinkText: PropTypes.func,
  onUpdateLinkUrl: PropTypes.func,
  onToggleEditMode: PropTypes.func,
  onSwapCustomLinks: PropTypes.func,
  onCancelCustomLinkEdit: PropTypes.func,
  onSaveNewLinkClick: PropTypes.func,
  onUpdateSingleCustomLink: PropTypes.func,
};

GridPosts.defaultProps = {
  loading: true,
  total: 0,
  gridPosts: [],
  publicGridUrl: '',
  previewGridUrl: '',
  isDisconnectedProfile: false,
  onChangePostUrl: () => {},
  onSavePostUrl: () => {},
  onImageClick: () => {},
  onImageClose: () => {},
  handleCopyToClipboard: () => {},
  profile: {},
  isValidItem: () => {},
  hasWriteAccess: false,
  customLinksDetails: {
    customLinks: [],
    maxCustomLinks: 0,
    buttonColor: null,
  },
  maxCustomLinks: 3,
  onUpdateCustomLinks: () => {},
  onUpdateCustomLinksColor: () => {},
  onUpdateCustomLinksButtonType: () => {},
  onDeleteCustomLink: () => {},
  onUpdateLinkText: () => {},
  onUpdateLinkUrl: () => {},
  onToggleEditMode: () => {},
  onSwapCustomLinks: () => {},
  onCancelCustomLinkEdit: () => {},
  onSaveNewLinkClick: () => {},
  onUpdateSingleCustomLink: () => {},
};

export default GridPosts;
